'use client';

// The house (tenant) switcher on the nav rail. A switch lands only once the server saved it, and the
// other houses are locked in place while the phone app is offline (BUG-2469).

import { ChevronDown, Building2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTenant } from './TenantProvider';
import { useAuth } from '../auth/AuthProvider';
import { useOfflineMode } from '../offline/OfflineModeProvider';
import { TENANT_SWITCH_OFFLINE } from '../offline/offline-copy';
import NeedsConnectionMark from '../offline/NeedsConnectionMark';
import { Tenant } from '@auctogon/admin-isomorphic/types/user';

interface UserTenant extends Tenant {
    isDefault: boolean;
}

interface DropdownMenuProps {
    userTenants: UserTenant[];
    currentTenant: UserTenant;
    isSuperAdmin: boolean;
    onSelectTenant: (tenant: UserTenant) => void;
    /** The phone app is offline: the other houses are locked in place (BUG-2469). */
    isOffline: boolean;
    /** True while a switch waits for the server; the rows don't take another pick. */
    switching: boolean;
    /** The last switch's failure, shown inline under the list. */
    error: string | null;
}

/** Non-interactive tenant badge — used for loading, empty, and single-tenant display states. */
function TenantBadgeShell({
    collapsed,
    children,
}: {
    collapsed: boolean;
    children: React.ReactNode;
}) {
    return (
        <div
            className={clsx(
                'border-b border-[#1a5a54]',
                collapsed ? 'px-2 py-3' : 'px-3 py-3'
            )}
        >
            <div
                className={clsx(
                    'w-full flex items-center gap-density-cluster bg-[#0d3633] rounded-lg border border-[#1a5a54]',
                    collapsed ? 'justify-center p-2' : 'px-3 py-2'
                )}
            >
                <Building2 className="h-4 w-4 text-secondary flex-shrink-0" />
                {!collapsed && children}
            </div>
        </div>
    );
}

/**
 * A house the operator can't switch to while offline: in place, disabled, with the no-wifi mark
 * (BUG-2077's one "needs a connection" treatment). `aria-disabled`, not `disabled`, so it stays
 * reachable with its reason. Its label is `slate-50` at 60% on the menu's `#0d3633`; WCAG 2.2 1.4.3
 * exempts a disabled control, and the mark plus `aria-disabled` are the second signal.
 */
function LockedTenantRow({ tenant }: { tenant: UserTenant }) {
    return (
        // eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu row on the dark nav rail (offline, needs connection)
        <button
            type="button"
            aria-disabled="true"
            data-state="needs-connection"
            data-testid={`tenant-selector-option-${tenant.id}`}
            title={TENANT_SWITCH_OFFLINE}
            className="w-full flex items-start gap-density-cluster px-3 py-2 text-left text-slate-50/60 cursor-default"
        >
            <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
                <div className="text-body font-medium truncate">{tenant.name}</div>
                <div className="text-caption truncate">{tenant.slug}</div>
            </div>
            <NeedsConnectionMark className="mt-0.5" />
        </button>
    );
}

function DropdownMenu({ userTenants, currentTenant, isSuperAdmin, onSelectTenant, isOffline, switching, error }: DropdownMenuProps) {
    return (
        <div data-testid="tenant-selector-menu" className="mt-2 bg-[#0d3633] border border-[#1a5a54] rounded-lg shadow-lg overflow-hidden">
            {isOffline && (
                // slate-300 (#cbd5e1) on #0d3633 ....... 8.88:1  text ✓ (needs 4.5)
                <div data-testid="tenant-selector-offline" className="px-3 py-2 text-caption text-slate-300 border-b border-[#1a5a54]">
                    {TENANT_SWITCH_OFFLINE}
                </div>
            )}
            <div className="max-h-64 overflow-y-auto" aria-busy={switching}>
                {userTenants.map((tenant) =>
                    isOffline && currentTenant.id !== tenant.id ? (
                        <LockedTenantRow key={tenant.id} tenant={tenant} />
                    ) : (
                        // eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu row on the dark nav rail: picks a tenant, no classic variant fits the rail
                        <button
                            type="button"
                            key={tenant.id}
                            data-testid={`tenant-selector-option-${tenant.id}`}
                            disabled={switching}
                            onClick={() => onSelectTenant(tenant)}
                            className={clsx(
                                'w-full flex items-start gap-density-cluster px-3 py-2 text-left transition-colors disabled:cursor-wait',
                                currentTenant.id === tenant.id
                                    ? 'bg-[#1a5a54] text-secondary'
                                    : 'text-slate-50 hover:bg-[#1a5a54]',
                            )}
                        >
                            <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-body font-medium truncate">
                                    {tenant.name}
                                </div>
                                <div className="text-caption text-[#D3D3D3]/70 truncate">
                                    {tenant.slug}
                                </div>
                            </div>
                            {tenant.isDefault && (
                                <span className="text-caption text-secondary flex-shrink-0">
                                    Default
                                </span>
                            )}
                        </button>
                    ),
                )}
            </div>
            {error && (
                // #fca5a5 (red-300) on #0d3633 ....... 6.95:1  text ✓ (needs 4.5)
                <div role="alert" data-testid="tenant-selector-error" className="px-3 py-2 text-caption text-red-300 border-t border-[#1a5a54]">
                    {error}
                </div>
            )}
            {isSuperAdmin && (
                <div className="border-t border-[#1a5a54] px-3 py-2">
                    <div className="text-caption text-[#D3D3D3]/50 font-medium">
                        SUPER ADMIN MODE
                    </div>
                    <div className="text-caption text-[#D3D3D3]/70 mt-1">
                        {userTenants.length} tenant{userTenants.length !== 1 ? 's' : ''} available
                    </div>
                </div>
            )}
        </div>
    );
}

/** The rail button that opens the house list, showing the active house. */
function TriggerButton({ collapsed, name, isOpen, onClick }: { collapsed: boolean; name: string; isOpen: boolean; onClick: () => void }) {
    return (
        <>
        {/* eslint-disable-next-line designsystem/no-raw-button -- non-CTA menu trigger on the dark nav rail: opens the tenant list, no classic variant fits the rail */}
        <button
            type="button"
            data-testid="tenant-selector-trigger"
            onClick={onClick}
            title={collapsed ? name : undefined}
            className={clsx(
                'w-full flex items-center gap-density-cluster bg-[#0d3633] rounded-lg border border-[#1a5a54] transition-colors hover:bg-[#1a5a54] cursor-pointer',
                collapsed ? 'justify-center p-2' : 'justify-between px-3 py-2'
            )}
        >
            {collapsed ? (
                <Building2 className="h-4 w-4 text-secondary" />
            ) : (
                <>
                    <div className="flex items-center gap-density-cluster min-w-0 flex-1">
                        <Building2 className="h-4 w-4 text-secondary flex-shrink-0" />
                        <span className="text-body text-slate-50 font-medium truncate">
                            {name}
                        </span>
                    </div>
                    <ChevronDown
                        className={clsx(
                            'h-4 w-4 text-[#D3D3D3]/70 flex-shrink-0 transition-transform',
                            isOpen ? 'rotate-180' : ''
                        )}
                    />
                </>
            )}
        </button>
        </>
    );
}

/**
 * The switch in flight and its outcome (BUG-2469). The tenant changes only once the server saved
 * it; on failure it stays, and the error is kept for the list to show. Offline, a pick of another
 * house does nothing (the row is locked anyway).
 *
 * @param input.currentTenantId - the active house; picking it again is a no-op switch.
 * @param input.onLanded - runs after a switch the server saved.
 * @returns the in-flight flag, the error, the picker and an error reset.
 * @sideEffect `select` fires `PUT /api/user/active-tenant` through the session.
 */
function useTenantSwitch(input: { currentTenantId: string | null; onLanded: () => void }) {
    const { setCurrentTenant } = useTenant();
    const { isOffline } = useOfflineMode();
    const [switching, setSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const select = async (tenant: UserTenant) => {
        if (switching || (isOffline && tenant.id !== input.currentTenantId)) return;
        setError(null);
        setSwitching(true);
        const outcome = await setCurrentTenant(tenant);
        setSwitching(false);
        if (!outcome.success) {
            setError(outcome.error);
            return;
        }
        input.onLanded();
    };

    return { isOffline, switching, error, select, clearError: () => setError(null) };
}

interface TenantSelectorProps {
    /** When true, render an icon-only button; dropdown slides out to the right. */
    collapsed?: boolean;
}

export default function TenantSelector({ collapsed = false }: TenantSelectorProps) {
    const { currentTenant, userTenants, loading } = useTenant();
    const { isSuperAdmin } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const router = useRouter();
    const { isOffline, switching, error, select, clearError } = useTenantSwitch({
        currentTenantId: currentTenant?.id ?? null,
        onLanded: () => {
            setIsOpen(false);
            // A switch always lands on the dashboard (BL-1664 RAC 4; Michael Lawrie,
            // 2026-09-14). Page state lives in the URL, and a term or filter that meant
            // something on tenant A is noise on tenant B, so the switch drops the page.
            router.push('/');
        },
    });

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close the dropdown when the sidebar collapses/expands so it doesn't jump
    // between the right-popout (collapsed) and inline-below (expanded) positions.
    const [prevCollapsed, setPrevCollapsed] = useState(collapsed);
    if (collapsed !== prevCollapsed) {
        setPrevCollapsed(collapsed);
        setIsOpen(false);
    }

    // Initial/default selection is owned entirely by TenantProvider (saved pref →
    // server isDefault → alphabetical-first, via a non-persisting setter). This
    // component must NOT auto-select a fallback: an earlier version persisted
    // userTenants[0] ("Acme") here, poisoning the saved preference and reverting
    // users to Acme on hard refresh (BUG-704). setCurrentTenant is now only called
    // from handleSelectTenant (an explicit click); the shell below covers the gap.

    if (loading) {
        return (
            <TenantBadgeShell collapsed={collapsed}>
                <div className="h-4 w-24 bg-[#1a5a54] animate-pulse rounded" />
            </TenantBadgeShell>
        );
    }

    // If no current tenant yet but we have tenants available, show the loading shell
    // while TenantProvider promotes the correct default tenant.
    if (!currentTenant && userTenants.length > 0) {
        return (
            <TenantBadgeShell collapsed={collapsed}>
                <div className="h-4 w-24 bg-[#1a5a54] animate-pulse rounded" />
            </TenantBadgeShell>
        );
    }

    // If no tenants at all
    if (!currentTenant || userTenants.length === 0) {
        return (
            <TenantBadgeShell collapsed={collapsed}>
                <span className="text-body text-[#D3D3D3]/70">No tenant assigned</span>
            </TenantBadgeShell>
        );
    }

    const isDisabled = userTenants.length <= 1 && !isSuperAdmin;

    // Single-tenant, non-Super-Admin users have nothing to switch to — render a
    // static label rather than a disabled button so there's no hover effect or
    // "not-allowed" cursor on the tenant name.
    if (isDisabled) {
        return (
            <TenantBadgeShell collapsed={collapsed}>
                <span className="text-body text-slate-50 font-medium truncate">
                    {currentTenant.name}
                </span>
            </TenantBadgeShell>
        );
    }

    /** Open or close the list; either way the last switch's error is cleared. */
    const toggleOpen = () => {
        setIsOpen(!isOpen);
        clearError();
    };

    const menu = (
        <DropdownMenu
            userTenants={userTenants}
            currentTenant={currentTenant}
            isSuperAdmin={isSuperAdmin}
            onSelectTenant={(tenant) => void select(tenant)}
            isOffline={isOffline}
            switching={switching}
            error={error}
        />
    );

    return (
        <div
            className={clsx(
                'border-b border-[#1a5a54] relative',
                collapsed ? 'px-2 py-3' : 'px-3 py-3'
            )}
            ref={dropdownRef}
        >
            <TriggerButton collapsed={collapsed} name={currentTenant.name} isOpen={isOpen} onClick={toggleOpen} />

            {isOpen && (
                collapsed ? (
                    <div className="absolute left-full top-3 ml-2 w-64 z-20">{menu}</div>
                ) : (
                    menu
                )
            )}
        </div>
    );
}
